"use client";
import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { Input } from "../../@/components/ui/input";
import { Form, FormField } from "@/components/ui/form";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "../../@/components/ui/field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../@/components/ui/select";
import { Textarea } from "../../@/components/ui/textarea";
import { Plus, Info } from "lucide-react";
import { File as FileIcon, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { api_endpoint } from "@/constants/constant";
import { useAuth } from "@clerk/clerk-react";
import { ClipLoader } from "react-spinners";
import MarkdownEditorDialog from "../components/MarkdownEditorDialog";
import { useR2Upload } from "../lib/useR2Upload.js";
import { cn } from "@/lib/utils";

const projectTypes = [
  { value: "capstone", label: "Capstone", score: 250 },
  { value: "standard", label: "Standard", score: 125 },
  { value: "challenge", label: "Challenge", score: 25 },
];

const addProjectSchema = z.object({
  title: z.string().min(1, "Project title is required"),
  // order: z.enum().min(1, "Order is required").regex(/^\d+$/, "Order must be a number (e.g. 1, 2, 3)"),
  githubUrl: z
    .string()
    .url("Please enter a valid GitHub URL")
    .optional()
    .or(z.literal("")),
  type: z.enum(["capstone", "standard", "challenge"], {
    required_error: "Please select a project type",
  }),
  score: z.coerce
    .number()
    .int({ message: "Points must be an integer" })
    .min(1, { message: "Points must be greater than 0" }),
  passmark: z.coerce
    .number()
    .int({ message: "Passmark must be an integer" })
    .min(1, { message: "Passmark is required" })
    .max(100, { message: "Passmark cannot be more than 100" }),
  description: z.string().min(1, "Content is required"),
  rubric: z.instanceof(File).optional(),
});

export function AddProjectDialog({
  topicId,
  courseName,
  onProjectAdded,
  ...rest
}) {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [markdownEditorOpen, setMarkdownEditorOpen] = useState(false);
  const { getToken } = useAuth();
  const { isUploading: isRubricUploading, uploadFile: uploadRubric } =
    useR2Upload();
  const [rubricFile, setRubricFile] = useState(null);

  const form = useForm({
    resolver: zodResolver(addProjectSchema),
    defaultValues: {
      title: "",
      // order: "",
      // githubUrl: "",
      type: undefined,
      score: "",
      passmark: "",
      description: "",
      rubric: undefined,
    },
  });

  const watchedType = form.watch("type");

  // Markdown editor handlers
  const handleMarkdownEditorClose = () => {
    setMarkdownEditorOpen(false);
  };

  const handleMarkdownSave = (value) => {
    form.setValue("description", value);
    setMarkdownEditorOpen(false);
  };

  const handleOpenMarkdownEditor = () => {
    setMarkdownEditorOpen(true);
  };

  // Auto-update points when type changes
  useEffect(() => {
    if (watchedType) {
      const selectedType = projectTypes.find(
        (type) => type.value === watchedType,
      );
      if (selectedType) {
        form.setValue("score", selectedType.score);
      }
    }
  }, [watchedType, form]);

  const onSubmit = async (values) => {
    try {
      setIsSubmitting(true);

      let rubricLink = null;
      if (rubricFile) {
        const projectNameSlug = values.title.toLowerCase().replace(/\s+/g, "-");
        const fileExtension = rubricFile.name.split(".").pop();
        const newFileName = `rubric.${fileExtension}`;
        const rubricPath = `rubrics/projects/${projectNameSlug}/${newFileName}`;
        rubricLink = await uploadRubric(rubricFile, rubricPath);
        if (!rubricLink) {
          // Upload failed
          setIsSubmitting(false);
          return;
        }
      }
      const token = await getToken();
      if (!token) return;
      if (!topicId) {
        toast.error("No topic selected.");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        ...values,
        topic_id: Number(topicId),
        rubric_link: rubricLink,
      };

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        api_endpoint + "/project/create",
        payload,
        reqConfig,
      );
      const project = response.data.project;
      console.log("Project created:", project);
      toast.success(`Added project "${values.title}" successfully!`);
      setOpen(false);
      form.reset(); // Reset form after successful submission
      setRubricFile(null);
      if (onProjectAdded) onProjectAdded(project);
    } catch (error) {
      console.error("Failed to create project:", error);
      console.error("Server response:", error?.response?.data);
      const serverMsg =
        error?.response?.data?.message ||
        (error?.response?.data && JSON.stringify(error.response.data)) ||
        error.message ||
        "Failed to add project. Please try again.";
      toast.error(serverMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    setRubricFile(null);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="">
          Add Project
          <Plus className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-(--surface-panel) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Add New Project
          </DialogTitle>
          <DialogDescription className="font-body text-(--neutral-600)">
            Create a new project with content and GitHub repository link.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
            id="add-project-form"
          >
            <div className="">
              <FormField
                control={form.control}
                name="title"
                className="w-full"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Project Title *</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="Enter project title"
                        className={cn(
                          "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                )}
              />

              {/* <FormField
                control={form.control}
                name="order"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>
                      Order
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Optional
                      </div>
                    </FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="e.g. 1, 2, 3"
                        type="number"
                        min="1"
                        className={cn(
                          "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                        )}
                      />
                      <FieldError errors={[]} />
                    </FieldContent>
                  </Field>
                )}
              /> */}
            </div>

            {/* <FormField
              control={form.control}
              name="githubUrl"
              render={({ field, fieldState }) => (
                <Field>
                  <FieldLabel>
                    GitHub URL
                    <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    <Input
                      {...field}
                      placeholder="https://github.com/username/repository"
                      type="url"
                      className={cn(
                        "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                      )}
                    />
                    <FieldError errors={[]} />
                  </FieldContent>
                </Field>
              )}
            /> */}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
              <div>
                <FormField
                  control={form.control}
                  name="type"
                  className="w-full"
                  render={({ field, fieldState }) => (
                    <Field>
                      <FieldLabel>Project Type *</FieldLabel>
                      <FieldContent>
                        <Select
                          value={field.value || ""}
                          onValueChange={field.onChange}
                        >
                          <SelectTrigger
                            className={cn(
                              "font-body w-[215px] bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) mt-1",
                              fieldState.error &&
                                "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                            )}
                          >
                            <SelectValue
                              placeholder="Select project type"
                              className="font-body text-(--neutral-900)"
                            />
                          </SelectTrigger>
                          <SelectContent className="bg-(--surface-panel) border border-(--neutral-300) dark:border-[var(--neutral-700)]">
                            {projectTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                <div className="flex items-center justify-between w-full">
                                  <span className="font-body">
                                    {type.label}
                                  </span>
                                  <span className="text-xs text-(--neutral-500) ml-2 font-body">
                                    {type.score} pts
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FieldError
                          errors={
                            fieldState.error
                              ? [{ message: "This is required" }]
                              : []
                          }
                        />
                      </FieldContent>
                    </Field>
                  )}
                />
              </div>
              <FormField
                control={form.control}
                name="score"
                className="mt-2"
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel>Max Score *</FieldLabel>
                    <FieldContent>
                      <Input
                        {...field}
                        placeholder="Enter points"
                        type="number"
                        min="1"
                        className={cn(
                          "font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) mt-1",
                          fieldState.error &&
                            "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                        )}
                      />
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                      <div className="flex justify-center text-center text-xs text-(--neutral-500) font-body">
                        Auto-filled based on project type, <br />
                        but can be manually adjusted
                      </div>
                    </FieldContent>
                  </Field>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="passmark"
              className="w-full"
              render={({ field, fieldState }) => {
                const value = field.value || 0;
                const fillPercent = Math.min(Math.max(value, 0), 100);

                return (
                  <Field>
                    <FieldLabel>Passmark (%) *</FieldLabel>
                    <FieldContent>
                      <div className="flex items-center gap-3 mt-1">
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={value}
                          onChange={(e) => {
                            let val = Number(e.target.value);
                            if (isNaN(val)) val = 0;
                            if (val < 0) val = 0;
                            if (val > 100) val = 100;
                            field.onChange(val);
                          }}
                          className={cn(
                            "w-16 border border-(--neutral-300) rounded px-2 py-1 text-sm text-center bg-(--surface-panel) text-(--neutral-900)",
                            fieldState.error &&
                              "border-(--color-destructive)",
                          )}
                        />
                        <div className="flex-1">
                          <input
                            type="range"
                            min={0}
                            max={100}
                            value={value}
                            onChange={(e) =>
                              field.onChange(Number(e.target.value))
                            }
                            className="w-full h-2 bg-(--neutral-200) rounded-lg appearance-none cursor-pointer"
                            style={{
                              background: `linear-gradient(to right, #ef4444 0%, #ef4444 ${fillPercent}%, #e5e7eb ${fillPercent}%, #e5e7eb 100%)`,
                            }}
                          />
                        </div>
                      </div>
                      <FieldError
                        errors={
                          fieldState.error
                            ? [{ message: "This is required" }]
                            : []
                        }
                      />
                    </FieldContent>
                  </Field>
                );
              }}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field, fieldState }) => (
                <Field>
                  <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <FieldLabel>
                      Content *
                      <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                        <Info className="w-3 h-3" />
                        Markdown supported
                      </div>
                    </FieldLabel>
                    <Button
                      type="button"
                      onClick={handleOpenMarkdownEditor}
                      size=""
                      variant="outline"
                      className=""
                    >
                      Edit in Fullscreen
                    </Button>
                  </div>
                  <FieldContent>
                    <Textarea
                      {...field}
                      rows={8}
                      className={cn(
                        "min-h-50 resize-none bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) focus:ring-0 font-body mt-1",
                        fieldState.error &&
                          "border-(--color-destructive) focus-visible:ring-(--color-primary)",
                      )}
                      placeholder="Enter project description and requirements using Markdown"
                    />
                    <FieldError
                      errors={
                        fieldState.error
                          ? [{ message: "This is required" }]
                          : []
                      }
                    />
                    <div className="text-xs text-(--neutral-500) mt-1">
                      Use Markdown syntax for formatting. Code blocks, links,
                      lists, and emphasis are supported.
                    </div>
                  </FieldContent>
                </Field>
              )}
            />

            <FormField
              control={form.control}
              name="rubric"
              render={({ field }) => (
                <Field>
                  <FieldLabel>
                    Rubric
                    <div className="flex items-center gap-1 text-xs text-(--neutral-500) font-body">
                      <Info className="w-3 h-3" />
                      Optional
                    </div>
                  </FieldLabel>
                  <FieldContent>
                    {rubricFile ? (
                      <div className="flex items-center justify-between p-2 border rounded-md bg-gray-50">
                        <div className="flex items-center gap-2">
                          <FileIcon className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium">
                            {rubricFile.name}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => {
                            setRubricFile(null);
                            field.onChange(undefined);
                          }}
                          type="button"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx,.md"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          field.onChange(file);
                          setRubricFile(file);
                        }}
                        className="font-body bg-(--surface-panel) border border-(--neutral-300) text-(--neutral-900) placeholder:text-(--neutral-500) mt-1"
                      />
                    )}
                    <FieldError errors={[]} />
                  </FieldContent>
                </Field>
              )}
            />
            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
                                    <div className="text-sm text-blue-800">
                                        <p className="font-medium mb-1">Project Types</p>
                                        <ul className="space-y-1 text-xs">
                                            <li><strong>Capstone (250 pts):</strong> Major comprehensive projects</li>
                                            <li><strong>Standard (125 pts):</strong> Regular practice projects</li>
                                            <li><strong>Challenge (25 pts):</strong> Quick coding challenges</li>
                                        </ul>
                                    </div>
                                </div>
                            </div> */}
          </form>
        </Form>

        <DialogFooter>
          <DialogClose asChild>
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={isSubmitting || isRubricUploading}
              className=""
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            form="add-project-form"
            variant=""
            className=""
            disabled={isSubmitting || isRubricUploading}
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="var(--neutral-50)" />
              </span>
            ) : (
              "Add Project"
            )}
          </Button>
        </DialogFooter>

        {/* Markdown Editor Dialog - positioned outside DialogContent but inside Dialog */}
        <MarkdownEditorDialog
          open={markdownEditorOpen}
          initialValue={form.watch("description") || ""}
          onClose={handleMarkdownEditorClose}
          onSave={handleMarkdownSave}
          contentFolder={form
            .watch("title")
            ?.toLowerCase()
            .replace(/\s+/g, "-")}
          contentName={form.watch("title")}
        />
      </DialogContent>
    </Dialog>
  );
}
